import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { ServiceClientService } from '../services/service-client.service';
import { environment } from '../../environments/environment';

export interface RuleMatch {
  type: string; // EXACT, STARTS_WITH
  value: string;
}

export interface RuleSuggestion {
  option: number;
  suggestion: string; // ERROR, WARNING, CORRECT
  isMcqCheckedRequired?: boolean;
  requiredPreReqAnswer?: string;
  isAgainstOrthoGroup?: boolean;
}

export interface Rule {
  id: string;
  match: RuleMatch;
  alertLabel?: string; // RECOMMENDATION, SUGGESTION
  statusType?: string;
  considerMessage?: string;
  remindersMessage?: string;
  suggestions?: RuleSuggestion[];
}

export interface CreateRulePayload {
  rules: Rule[];
}

/** Document row from GET /rule/findAll/v1 (flat GG fields and/or legacy nested `rules`). */
export interface ApiRuleDocument {
  _id?: string;
  id?: string;
  version?: string;
  agency?: string;
  group?: string;
  visitType?: string;
  ruleType?: string;
  title?: string;
  GG1?: string;
  GG2?: string;
  GG1Values?: string[];
  GGOtherValues?: string[];
  GGOperator?: string;
  GGType?: string;
  GGMessage?: string;
  __v?: number;
  rules?: Rule[];
  createdAt?: string;
  updatedAt?: string;
  lastModifiedStamp?: string;
  [key: string]: unknown;
}

export interface FindAllRulesQuery {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

export interface FindAllRulesResponse {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: ApiRuleDocument[];
  totalItems?: number;
  total?: number;
  /** Server may return these as strings (e.g. "1", "30"). */
  currentPage?: string;
  limit?: string;
  totalPages?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  content?: {
    data?: ApiRuleDocument[];
    pagination?: { total?: number };
    totalElements?: number;
  };
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RulesService {
  private rulesStore = new BehaviorSubject<any[]>([]);

  constructor(private readonly serviceClientService: ServiceClientService) {}

  getAll(options: FindAllRulesQuery): Observable<FindAllRulesResponse> {
    const sortBy = options.sortBy ?? 'createdAt';
    const sortOrder = options.sortOrder ?? 'desc';

    let params = new HttpParams()
      .set('page', String(options.page))
      .set('limit', String(options.limit))
      .set('sortBy', sortBy)
      .set('sortOrder', sortOrder);

    if (options.search) {
      params = params.set('search', options.search);
    }

    return this.serviceClientService
      .get<FindAllRulesResponse>(environment.endPoints.findAllRules, params)
      .pipe(map((res) => this.normalizeFindAllResponse(res)));
  }

  private normalizeFindAllResponse(raw: any): FindAllRulesResponse {
    if (!raw || typeof raw !== 'object') {
      return { success: false, data: [], totalItems: 0 };
    }

    const nestedDataRoot = raw.content?.data;
    const data: ApiRuleDocument[] = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.content?.data)
        ? raw.content.data
        : Array.isArray(nestedDataRoot?.data)
          ? nestedDataRoot.data
        : Array.isArray(raw.items)
          ? raw.items
          : [];

    // Normalize field names for the existing UI.
    // The backend uses `lastModifiedStamp`, while the table expects `updatedAt`.
    const normalizedData: ApiRuleDocument[] = data.map((item) => {
      if (item?.updatedAt == null && item?.lastModifiedStamp != null) {
        return {
          ...item,
          updatedAt: item.lastModifiedStamp,
        };
      }
      return item;
    });

    const totalItems =
      raw.totalItems ??
      raw.total ??
      raw.content?.pagination?.total ??
      raw.content?.totalElements ??
      nestedDataRoot?.totalItems ??
      normalizedData.length;

    const success =
      raw.success !== false &&
      raw.errorMessage == null &&
      (raw.statusCode === undefined || (raw.statusCode >= 200 && raw.statusCode < 300));

    return {
      ...raw,
      success,
      data: normalizedData,
      totalItems,
    };
  }

  createRule(payload: Rule): Observable<any> {
    return this.serviceClientService.post<Rule, any>(
      environment.endPoints.createRule,
      payload,
    );
  }

  updateRule(id: string | number, payload: Rule): Observable<any> {
    const endpoint = `${environment.endPoints.updateRule}/${encodeURIComponent(String(id))}`;
    return this.serviceClientService.put<Rule, any>(endpoint, payload);
  }

  deleteRule(id: string | number): Observable<any> {
    const endpoint = `${environment.endPoints.updateRule}/${encodeURIComponent(String(id))}`;
    return this.serviceClientService.delete<any>(endpoint);
  }
}
